import { useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import "./ImageLightbox.css";

// Click-to-enlarge lightbox, shared across components. Each consumer owns its
// own state via useImageLightbox() and renders <ImageLightbox> in its own JSX
// tree -- no global/shared state between consumers.
export function useImageLightbox() {
  // { images: [{url, label}], index } | null
  const [lightbox, setLightbox] = useState(null);

  const openLightbox = useCallback((images, index) => {
    if (images && images.length) setLightbox({ images, index });
  }, []);
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const stepLightbox = useCallback((delta) => {
    setLightbox((lb) =>
      lb
        ? { ...lb, index: (lb.index + delta + lb.images.length) % lb.images.length }
        : lb,
    );
  }, []);

  // Keyboard controls while open (Esc closes, ←/→ navigate).
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") stepLightbox(-1);
      else if (e.key === "ArrowRight") stepLightbox(1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox, closeLightbox, stepLightbox]);

  return { lightbox, openLightbox, closeLightbox, stepLightbox };
}

export default function ImageLightbox({ lightbox, onClose, onStep }) {
  if (!lightbox) return null;
  const { images, index } = lightbox;

  return (
    <div
      className="img-lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Bildvorschau"
      onClick={onClose}
    >
      <button
        type="button"
        className="img-lightbox-close"
        onClick={onClose}
        aria-label="Schließen"
      >
        <X size={20} aria-hidden="true" />
      </button>
      {images.length > 1 && (
        <button
          type="button"
          className="img-lightbox-nav img-lightbox-prev"
          onClick={(e) => {
            e.stopPropagation();
            onStep(-1);
          }}
          aria-label="Vorheriges Bild"
        >
          <ChevronLeft size={24} aria-hidden="true" />
        </button>
      )}
      <figure
        className="img-lightbox-figure"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[index].url}
          alt={images[index].label || "Bild"}
          className="img-lightbox-img"
        />
        <figcaption className="img-lightbox-caption">
          {images[index].label}
          {images.length > 1 && ` · ${index + 1} / ${images.length}`}
        </figcaption>
      </figure>
      {images.length > 1 && (
        <button
          type="button"
          className="img-lightbox-nav img-lightbox-next"
          onClick={(e) => {
            e.stopPropagation();
            onStep(1);
          }}
          aria-label="Nächstes Bild"
        >
          <ChevronRight size={24} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
